import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { authStore, useAuthStore } from '@/auth/authStore';
import { queryClient } from '@/app/queryClient';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001';

/**
 * Shared Axios instance for the Annova ERP API.
 *
 * A request interceptor attaches the bearer token from the auth store and, when the token is about
 * to expire (or has expired while the tab was asleep), refreshes it first so the request never goes
 * out with a dead token. A response interceptor still handles a 401 the proactive check missed:
 * one single-flight refresh, then the original request again, signing the user out only if the
 * refresh itself fails. Tokens live in localStorage shared by every tab; the store is re-read from
 * it before any refresh, so a tab that wakes after another tab already rotated the refresh token
 * uses the rotated one instead of the stale copy in memory.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// A bare client (no interceptors) used to call the refresh endpoint without recursion.
const bareClient: AxiosInstance = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });

/** Refresh when the access token has less than this long to live. */
const REFRESH_AHEAD_MS = 3 * 60_000;

/** The `exp` claim of a JWT as a millisecond timestamp, or null when it cannot be read. */
export function tokenExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch { return null; }
}

function expiringSoon(token: string | null | undefined): boolean {
  const exp = tokenExpiry(token);
  return exp !== null && exp - Date.now() < REFRESH_AHEAD_MS;
}

/** Another tab may have rotated the tokens; take what localStorage holds now. */
function rehydrate(): void {
  try { void useAuthStore.persist.rehydrate(); } catch { /* storage unavailable: keep what is in memory */ }
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  rehydrate();
  const before = authStore.getState();
  // Another tab already refreshed: its token is fresh, so use it and do not spend the refresh token again.
  if (before.accessToken && !expiringSoon(before.accessToken)) return before.accessToken;
  const { refreshToken } = before;
  if (!refreshToken) return null;

  try {
    const { data } = await bareClient.post('/api/v1/auth/refresh', { refreshToken });
    authStore.getState().setTokens(data.accessToken, data.refreshToken);
    return data.accessToken as string;
  } catch {
    // The refresh token may have been used by another tab a moment ago; if that tab stored a live
    // token, this tab carries on with it. Otherwise the session is over.
    rehydrate();
    const after = authStore.getState();
    if (after.accessToken && after.accessToken !== before.accessToken && !expiringSoon(after.accessToken)) return after.accessToken;
    authStore.getState().clear();
    return null;
  }
}

/** One refresh at a time, shared by every caller that finds the token stale. */
function refreshOnce(): Promise<string | null> {
  refreshPromise ??= refreshAccessToken().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/**
 * Re-issues the tokens now, whatever the expiry. After a role or permission change the claims in
 * the access token must catch up with the profile, or the server keeps refusing what the client
 * now offers.
 */
export async function refreshTokensNow(): Promise<string | null> {
  const { refreshToken } = authStore.getState();
  if (!refreshToken) return null;
  try {
    const { data } = await bareClient.post('/api/v1/auth/refresh', { refreshToken });
    authStore.getState().setTokens(data.accessToken, data.refreshToken);
    return data.accessToken as string;
  } catch {
    return null;
  }
}

const isAuthUrl = (url: string) => url.includes('/auth/login') || url.includes('/auth/refresh');

apiClient.interceptors.request.use(async (config) => {
  let token = authStore.getState().accessToken;
  const url = config.url ?? '';
  if (token && !isAuthUrl(url) && expiringSoon(token)) {
    // Refresh before sending rather than after a 401: the request goes out once, with a live token.
    token = (await refreshOnce()) ?? token;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // The installed app names its device (ToR 4.3.2): a blocked device is turned away by the server whatever its token.
    // The facility a person is working at, for the Health module; the server proves it against their assignments (docs §279).
    try { const facility = localStorage.getItem('annova.hospital.facility'); if (facility && url.includes('/hospital')) config.headers['X-Facility-Id'] = facility; } catch { /* storage unavailable */ }
    try { const device = localStorage.getItem('annova.device.id'); if (device) config.headers['X-Device-Id'] = device; } catch { /* storage unavailable */ }
  }
  // For multipart uploads, drop the default JSON content type so the browser sets
  // `multipart/form-data` with the correct boundary (otherwise the server can't parse the form).
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status as number | undefined;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url ?? '';

    // A 403 carrying the ModuleDisabled code means the organisation's modules changed since the
    // client last asked: refetch them, so the open page swaps to its "module not enabled" card.
    if (status === 403 && (error?.response?.data as { code?: string } | undefined)?.code === 'ModuleDisabled') {
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
    }

    // The acceptable-use agreement must be accepted before anything else answers (ToR 1.0(xiii)).
    if (status === 403 && (error?.response?.data as { code?: string } | undefined)?.code === 'PolicyAcceptanceRequired') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/accept-policy')) window.location.assign('/accept-policy');
    }

    // The API refuses everything but the auth endpoints until an expired or reset password is changed (ToR 4.1.3).
    if (status === 403 && (error?.response?.data as { code?: string } | undefined)?.code === 'PasswordChangeRequired') {
      const current = authStore.getState().user;
      if (current && !current.mustChangePassword) authStore.setState({ user: { ...current, mustChangePassword: true } });
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/change-password')) window.location.assign('/change-password');
    }

    // Only attempt a refresh on a 401, once per request, and never for the auth endpoints.
    if (status === 401 && original && !original._retry && !isAuthUrl(url)) {
      original._retry = true;
      const newToken = await refreshOnce();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      // Refresh failed → session cleared; bounce to login.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }

    return Promise.reject(error);
  },
);

// A tab that wakes from sleep or comes back to the foreground refreshes a stale token straight
// away, before the queries it re-runs on focus go out; and a token rotated in another tab is
// picked up here as soon as that tab stores it.
if (typeof window !== 'undefined') {
  const wake = () => {
    if (document.visibilityState !== 'visible') return;
    rehydrate();
    const { accessToken, refreshToken } = authStore.getState();
    if (accessToken && refreshToken && expiringSoon(accessToken)) void refreshOnce();
  };
  window.addEventListener('focus', wake);
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('storage', (e) => { if (e.key === 'zsic-sbp-auth') rehydrate(); });
}
